import React, { useState } from 'react';
import {
  Typography,
  Button,
  Input,
  FormField,
  Textarea,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  AccordionFooter,
  AvatarText,
  Toggle,
  Badge,
  FlexColumn,
  FlexRow,
  Row as GridRow,
  Col,
} from 'design-system';
import { CheckIcon, StarIcon } from '../../../design-system/components/Icons';

const Separator = ({ className = '' }) => (
  <div className={['separator', className].filter(Boolean).join(' ')} />
);

const ACCORDION_RATING_ROWS = [
  { label: 'London', score: '4.8', variant: 'positive' },
  { label: 'Leeds', score: '4.2', variant: 'information' },
  { label: 'Birmingham', score: '3.9', variant: 'notice' },
  { label: 'Glasgow', score: '4.5', variant: 'positive' },
  { label: 'Bristol', score: '4.1', variant: 'information' },
];

function AccordionOverviewRatings() {
  return (
    <div
      style={{
        border: '1px solid #E5E7EB',
        borderRadius: 8,
        padding: 16,
        background: '#fff',
      }}
    >
      <Typography variant="headingSmall" className="mb-3">
        Overview ratings
      </Typography>
      <FlexColumn gap={12} align="stretch" fullWidth>
        {ACCORDION_RATING_ROWS.map((row) => (
          <FlexRow key={row.label} align="center" justify="between" fullWidth>
            <FlexRow align="center" gap={8} inline>
              <StarIcon size={16} filled aria-hidden />
              <Typography variant="bodyMedium">{row.label}</Typography>
            </FlexRow>
            <Badge variant={row.variant} emphasis="subtle">{row.score}</Badge>
          </FlexRow>
        ))}
      </FlexColumn>
    </div>
  );
}

export default function AccordionShowcase() {
  const [accordionName, setAccordionName] = useState('');
  const [accordionExpandedId, setAccordionExpandedId] = useState('ds-accordion-step-1');
  const [accordionCompleted, setAccordionCompleted] = useState({ 1: false, 2: false, 3: false });
  const [accordionFeedback, setAccordionFeedback] = useState('');
  const [accordionMessage, setAccordionMessage] = useState('');
  const [actionPanelToggle, setActionPanelToggle] = useState(false);

  const handleAccordionToggle = (stepId) => (nextExpanded) => {
    setAccordionExpandedId(nextExpanded ? stepId : null);
  };

  const handleAccordionComplete = (stepNum, nextStepId) => {
    setAccordionCompleted((prev) => ({ ...prev, [stepNum]: true }));
    if (nextStepId) {
      setAccordionExpandedId(nextStepId);
    } else {
      setAccordionExpandedId(null);
    }
  };

  const resolveAccordionStatus = (stepNum) => {
    if (accordionCompleted[stepNum]) return 'completed';
    if (accordionExpandedId === `ds-accordion-step-${stepNum}`) return 'active';
    return 'pending';
  };

  return (
    <GridRow>
      <Col xs={12}>
        <div className="design-system-section">
          <Typography variant="h1" className="section-title mb-4">
            Accordion
          </Typography>
          <Separator className="mb-5" />
          <Accordion id="ds-accordion-showcase">
            <AccordionItem
              id="ds-accordion-step-1"
              status={resolveAccordionStatus(1)}
              expanded={accordionExpandedId === 'ds-accordion-step-1'}
              onToggle={handleAccordionToggle('ds-accordion-step-1')}
            >
              <AccordionHeader
                title="Getting Started"
                description="Take a look at the overview resources prepared for you."
                stepNumber={1}
                totalSteps={3}
                showCompletedStatus={accordionCompleted[1]}
              />
              <AccordionBody>
                <FlexColumn gap={24} align="stretch" fullWidth>
                  <FormField label="Your name" helperText="Example form content inside the accordion body.">
                    <Input
                      id="ds-accordion-name"
                      placeholder="Enter your name"
                      value={accordionName}
                      onChange={(e) => setAccordionName(e.target.value)}
                    />
                  </FormField>
                  <AccordionOverviewRatings />
                </FlexColumn>
              </AccordionBody>
              <AccordionFooter>
                <Button
                  variant="primary"
                  size="base"
                  icon={<CheckIcon size={16} aria-hidden />}
                  onClick={() => handleAccordionComplete(1, 'ds-accordion-step-2')}
                >
                  I&apos;ve reviewed these
                </Button>
              </AccordionFooter>
            </AccordionItem>

            <AccordionItem
              id="ds-accordion-step-2"
              status={resolveAccordionStatus(2)}
              expanded={accordionExpandedId === 'ds-accordion-step-2'}
              onToggle={handleAccordionToggle('ds-accordion-step-2')}
            >
              <AccordionHeader
                title="Share Your Feedback"
                description="Your input helps us build a better experience for you."
                stepNumber={2}
                totalSteps={3}
                showCompletedStatus={accordionCompleted[2]}
              />
              <AccordionBody>
                <FlexColumn gap={16} align="stretch" fullWidth>
                  <Typography variant="headingSmall">Feedback</Typography>
                  <FormField label="Comments">
                    <Textarea
                      id="ds-accordion-feedback"
                      placeholder="Enter your feedback"
                      value={accordionFeedback}
                      onChange={(e) => setAccordionFeedback(e.target.value)}
                      rows={4}
                    />
                  </FormField>
                  <FlexRow align="center" justify="between" fullWidth>
                    <Typography variant="bodySmall" color="muted">
                      Enable email updates
                    </Typography>
                    <Toggle
                      checked={actionPanelToggle}
                      onChange={setActionPanelToggle}
                      size="md"
                      ariaLabel="Enable email updates"
                    />
                  </FlexRow>
                </FlexColumn>
              </AccordionBody>
              <AccordionFooter>
                <Button
                  variant="primary"
                  size="base"
                  icon={<CheckIcon size={16} aria-hidden />}
                  disabled={!accordionFeedback.trim()}
                  onClick={() => handleAccordionComplete(2, 'ds-accordion-step-3')}
                >
                  Send feedback
                </Button>
              </AccordionFooter>
            </AccordionItem>

            <AccordionItem
              id="ds-accordion-step-3"
              status={resolveAccordionStatus(3)}
              expanded={accordionExpandedId === 'ds-accordion-step-3'}
              onToggle={handleAccordionToggle('ds-accordion-step-3')}
            >
              <AccordionHeader
                title="Talk with leadership"
                description="Our leadership team is eager to hear your thoughts."
                stepNumber={3}
                totalSteps={3}
                showCompletedStatus={accordionCompleted[3]}
              />
              <AccordionBody>
                <FlexColumn gap={16} align="stretch" fullWidth>
                  <AvatarText
                    fallback="RD"
                    fallbackVariant="theme"
                    title="Rachel Doyle — Managing Director"
                    subtitle="rachel.doyle@example.com"
                  />
                  <FormField label="Your message" helperText="Your message goes directly to the leadership team.">
                    <Textarea
                      id="ds-accordion-message"
                      placeholder="Enter your message..."
                      value={accordionMessage}
                      onChange={(e) => setAccordionMessage(e.target.value)}
                      rows={5}
                    />
                  </FormField>
                </FlexColumn>
              </AccordionBody>
              <AccordionFooter>
                <Button
                  variant="primary"
                  size="base"
                  icon={<CheckIcon size={16} aria-hidden />}
                  disabled={!accordionMessage.trim()}
                  onClick={() => handleAccordionComplete(3, null)}
                >
                  Finish &amp; submit
                </Button>
              </AccordionFooter>
            </AccordionItem>
          </Accordion>
        </div>
      </Col>
    </GridRow>
  );
}
